/**
 * Propositional formulas: parsing, evaluating, printing and tabulating.
 *
 * Precedence, tightest first — the order most logic texts give, with the connectives
 * they leave out placed beside their nearest relatives:
 *
 *     NOT  (¬ ~ ! not, and a trailing ′)
 *     AND  (∧ & · * and, or two operands side by side)    NAND (↑ nand)
 *     XOR  (⊕ xor)
 *     OR   (∨ | + or)                                      NOR  (↓ nor)
 *     IMPLIES (→ -> => implies)           read right to left: p → q → r is p → (q → r)
 *     IFF  (↔ <-> <=> iff ≡)
 *
 * NAND and NOR are not associative, so chaining one without brackets is an error. Every
 * variable is one letter with optional digits (`p`, `x2`); a longer run of letters such as
 * `AB` is read as the product A·B unless it is a keyword.
 */
export type BinaryOp =
  | "and"
  | "or"
  | "xor"
  | "nand"
  | "nor"
  | "implies"
  | "iff";

export type Formula =
  | { kind: "var"; name: string }
  | { kind: "const"; value: boolean }
  | { kind: "not"; arg: Formula }
  | { kind: "bin"; op: BinaryOp; left: Formula; right: Formula };

export type LogicErrorCode =
  | "empty"
  | "too-long"
  | "character"
  | "caret"
  | "unexpected"
  | "end"
  | "bracket"
  | "ambiguous"
  | "too-many-variables";

export interface LogicError {
  code: LogicErrorCode;
  token: string;
}

export type LogicParse =
  | { ok: true; formula: Formula; variables: string[] }
  | { ok: false; error: LogicError };

export const MAX_VARIABLES = 8;
export const MAX_FORMULA_LENGTH = 300;

type Token =
  | { kind: "var"; name: string }
  | { kind: "const"; value: boolean }
  | { kind: "op"; op: BinaryOp }
  | { kind: "not" }
  | { kind: "postfix" }
  | { kind: "open" }
  | { kind: "close" };

const SYMBOLS: [string, Token][] = [
  ["<=>", { kind: "op", op: "iff" }],
  ["<->", { kind: "op", op: "iff" }],
  ["↔", { kind: "op", op: "iff" }],
  ["⇔", { kind: "op", op: "iff" }],
  ["≡", { kind: "op", op: "iff" }],
  ["⊙", { kind: "op", op: "iff" }],
  ["=>", { kind: "op", op: "implies" }],
  ["->", { kind: "op", op: "implies" }],
  ["→", { kind: "op", op: "implies" }],
  ["⇒", { kind: "op", op: "implies" }],
  ["⊃", { kind: "op", op: "implies" }],
  ["⊕", { kind: "op", op: "xor" }],
  ["⊻", { kind: "op", op: "xor" }],
  ["↮", { kind: "op", op: "xor" }],
  ["↑", { kind: "op", op: "nand" }],
  ["⊼", { kind: "op", op: "nand" }],
  ["↓", { kind: "op", op: "nor" }],
  ["⊽", { kind: "op", op: "nor" }],
  ["||", { kind: "op", op: "or" }],
  ["|", { kind: "op", op: "or" }],
  ["∨", { kind: "op", op: "or" }],
  ["+", { kind: "op", op: "or" }],
  ["&&", { kind: "op", op: "and" }],
  ["&", { kind: "op", op: "and" }],
  ["∧", { kind: "op", op: "and" }],
  ["·", { kind: "op", op: "and" }],
  ["⋅", { kind: "op", op: "and" }],
  ["*", { kind: "op", op: "and" }],
  ["¬", { kind: "not" }],
  ["~", { kind: "not" }],
  ["!", { kind: "not" }],
  ["'", { kind: "postfix" }],
  ["′", { kind: "postfix" }],
  ["’", { kind: "postfix" }],
  ["(", { kind: "open" }],
  ["[", { kind: "open" }],
  [")", { kind: "close" }],
  ["]", { kind: "close" }],
  ["⊤", { kind: "const", value: true }],
  ["⊥", { kind: "const", value: false }],
];

const KEYWORDS: Record<string, Token> = {
  not: { kind: "not" },
  and: { kind: "op", op: "and" },
  or: { kind: "op", op: "or" },
  xor: { kind: "op", op: "xor" },
  nand: { kind: "op", op: "nand" },
  nor: { kind: "op", op: "nor" },
  xnor: { kind: "op", op: "iff" },
  iff: { kind: "op", op: "iff" },
  implies: { kind: "op", op: "implies" },
  true: { kind: "const", value: true },
  false: { kind: "const", value: false },
};

const LETTERS = /^[A-Za-z]+/;
const DIGITS = /^\d+/;
const SPACE = /^\s+/;

class LogicFailure extends Error {
  readonly detail: LogicError;
  constructor(code: LogicErrorCode, token = "") {
    super(code);
    this.detail = { code, token };
  }
}

function readLetters(
  run: string,
  rest: string
): { tokens: Token[]; used: number } {
  const keyword = KEYWORDS[run.toLowerCase()];
  if (keyword) {
    return { tokens: [keyword], used: run.length };
  }
  const tokens: Token[] = [];
  let used = 0;
  [...run].forEach((letter, index) => {
    used += 1;
    let name = letter;
    if (index === run.length - 1) {
      const digits = DIGITS.exec(rest.slice(run.length));
      if (digits) {
        name += digits[0];
        used += digits[0].length;
      }
    }
    tokens.push({ kind: "var", name });
  });
  return { tokens, used };
}

function tokenize(text: string): Token[] {
  const tokens: Token[] = [];
  let rest = text;
  while (rest.length > 0) {
    const space = SPACE.exec(rest);
    if (space) {
      rest = rest.slice(space[0].length);
      continue;
    }
    if (rest[0] === "^") {
      throw new LogicFailure("caret", "^");
    }
    const letters = LETTERS.exec(rest);
    if (letters) {
      const read = readLetters(letters[0], rest);
      tokens.push(...read.tokens);
      rest = rest.slice(read.used);
      continue;
    }
    if (rest[0] === "0" || rest[0] === "1") {
      tokens.push({ kind: "const", value: rest[0] === "1" });
      rest = rest.slice(1);
      continue;
    }
    const symbol = SYMBOLS.find(([text]) => rest.startsWith(text));
    if (!symbol) {
      throw new LogicFailure("character", rest[0]);
    }
    tokens.push(symbol[1]);
    rest = rest.slice(symbol[0].length);
  }
  return tokens;
}

const AND_LEVEL: BinaryOp[] = ["and", "nand"];
const OR_LEVEL: BinaryOp[] = ["or", "nor"];

class Parser {
  private position = 0;
  private readonly tokens: Token[];

  constructor(tokens: Token[]) {
    this.tokens = tokens;
  }

  parse(): Formula {
    const result = this.iff();
    if (this.position < this.tokens.length) {
      const token = this.tokens[this.position];
      throw new LogicFailure(
        token.kind === "close" ? "bracket" : "unexpected",
        describe(token)
      );
    }
    return result;
  }

  private peek(): Token | undefined {
    return this.tokens[this.position];
  }

  private takeOp(ops: BinaryOp[]): BinaryOp | null {
    const token = this.peek();
    if (token?.kind === "op" && ops.includes(token.op)) {
      this.position += 1;
      return token.op;
    }
    return null;
  }

  private iff(): Formula {
    let left = this.implies();
    while (this.takeOp(["iff"])) {
      left = { kind: "bin", op: "iff", left, right: this.implies() };
    }
    return left;
  }

  private implies(): Formula {
    const left = this.orLevel();
    if (this.takeOp(["implies"])) {
      return { kind: "bin", op: "implies", left, right: this.implies() };
    }
    return left;
  }

  private chain(
    next: () => Formula,
    ops: BinaryOp[],
    strict: BinaryOp,
    implicit: boolean
  ): Formula {
    let left = next();
    let count = 0;
    let strictSeen = false;
    for (;;) {
      const op =
        this.takeOp(ops) ?? (implicit && this.startsOperand() ? "and" : null);
      if (op === null) {
        return left;
      }
      count += 1;
      strictSeen = strictSeen || op === strict;
      if (strictSeen && count > 1) {
        throw new LogicFailure("ambiguous", strict === "nand" ? "NAND" : "NOR");
      }
      left = { kind: "bin", op, left, right: next() };
    }
  }

  private orLevel(): Formula {
    return this.chain(() => this.xor(), OR_LEVEL, "nor", false);
  }

  private xor(): Formula {
    let left = this.andLevel();
    while (this.takeOp(["xor"])) {
      left = { kind: "bin", op: "xor", left, right: this.andLevel() };
    }
    return left;
  }

  private andLevel(): Formula {
    return this.chain(() => this.unary(), AND_LEVEL, "nand", true);
  }

  private startsOperand(): boolean {
    const token = this.peek();
    return (
      token !== undefined &&
      (token.kind === "var" ||
        token.kind === "const" ||
        token.kind === "open" ||
        token.kind === "not")
    );
  }

  private unary(): Formula {
    const token = this.peek();
    if (token?.kind === "not") {
      this.position += 1;
      return { kind: "not", arg: this.unary() };
    }
    let node = this.primary();
    while (this.peek()?.kind === "postfix") {
      this.position += 1;
      node = { kind: "not", arg: node };
    }
    return node;
  }

  private primary(): Formula {
    const token = this.peek();
    if (!token) {
      throw new LogicFailure("end");
    }
    this.position += 1;
    if (token.kind === "var") {
      return { kind: "var", name: token.name };
    }
    if (token.kind === "const") {
      return { kind: "const", value: token.value };
    }
    if (token.kind === "open") {
      const inner = this.iff();
      if (this.peek()?.kind !== "close") {
        throw new LogicFailure("bracket");
      }
      this.position += 1;
      return inner;
    }
    throw new LogicFailure(
      token.kind === "close" ? "bracket" : "unexpected",
      describe(token)
    );
  }
}

function describe(token: Token): string {
  switch (token.kind) {
    case "var":
      return token.name;
    case "op":
      return token.op;
    case "const":
      return token.value ? "1" : "0";
    case "open":
      return "(";
    case "close":
      return ")";
    case "postfix":
      return "′";
    default:
      return "¬";
  }
}

const NAME_ORDER = /^([A-Za-z])(\d*)$/;

export function compareVariables(a: string, b: string): number {
  const x = NAME_ORDER.exec(a);
  const y = NAME_ORDER.exec(b);
  if (!(x && y)) {
    return a.localeCompare(b);
  }
  const letters = x[1].toLowerCase().localeCompare(y[1].toLowerCase());
  if (letters !== 0) {
    return letters;
  }
  if (x[1] !== y[1]) {
    return x[1] === x[1].toLowerCase() ? -1 : 1;
  }
  return Number(x[2] || 0) - Number(y[2] || 0);
}

export function variablesOf(formula: Formula): string[] {
  const names = new Set<string>();
  const walk = (node: Formula): void => {
    if (node.kind === "var") {
      names.add(node.name);
    } else if (node.kind === "not") {
      walk(node.arg);
    } else if (node.kind === "bin") {
      walk(node.left);
      walk(node.right);
    }
  };
  walk(formula);
  return [...names].sort(compareVariables);
}

export function parseFormula(input: string): LogicParse {
  const text = input.trim();
  if (text === "") {
    return { ok: false, error: { code: "empty", token: "" } };
  }
  if (text.length > MAX_FORMULA_LENGTH) {
    return { ok: false, error: { code: "too-long", token: "" } };
  }
  try {
    const formula = new Parser(tokenize(text)).parse();
    const variables = variablesOf(formula);
    if (variables.length > MAX_VARIABLES) {
      return {
        ok: false,
        error: { code: "too-many-variables", token: String(MAX_VARIABLES) },
      };
    }
    return { ok: true, formula, variables };
  } catch (error) {
    if (error instanceof LogicFailure) {
      return { ok: false, error: error.detail };
    }
    throw error;
  }
}

export type Assignment = Record<string, boolean>;

export function evaluate(node: Formula, env: Assignment): boolean {
  switch (node.kind) {
    case "var":
      return env[node.name] ?? false;
    case "const":
      return node.value;
    case "not":
      return !evaluate(node.arg, env);
    default: {
      const l = evaluate(node.left, env);
      const r = evaluate(node.right, env);
      switch (node.op) {
        case "and":
          return l && r;
        case "or":
          return l || r;
        case "xor":
          return l !== r;
        case "nand":
          return !(l && r);
        case "nor":
          return !(l || r);
        case "implies":
          return !l || r;
        default:
          return l === r;
      }
    }
  }
}

// ---------------------------------------------------------------------------------------
// Printing
// ---------------------------------------------------------------------------------------

export type NotationStyle = "logic" | "boolean";

const PRECEDENCE: Record<BinaryOp, number> = {
  iff: 1,
  implies: 2,
  or: 3,
  nor: 3,
  xor: 4,
  and: 5,
  nand: 5,
};

const SYMBOL: Record<NotationStyle, Record<BinaryOp, string>> = {
  logic: {
    and: "∧",
    or: "∨",
    xor: "⊕",
    nand: "↑",
    nor: "↓",
    implies: "→",
    iff: "↔",
  },
  boolean: {
    and: "·",
    or: "+",
    xor: "⊕",
    nand: "↑",
    nor: "↓",
    implies: "→",
    iff: "↔",
  },
};

const ASSOCIATIVE: BinaryOp[] = ["and", "or", "xor", "iff"];

/**
 * The formula as text, with as few brackets as the precedence rules allow — or with every
 * binary operation bracketed (`full`), which shows exactly how it was read.
 */
export function formatFormula(
  node: Formula,
  style: NotationStyle = "logic",
  full = false
): string {
  switch (node.kind) {
    case "var":
      return node.name;
    case "const":
      return node.value ? "1" : "0";
    case "not": {
      const inner = formatFormula(node.arg, style, full);
      const atom =
        node.arg.kind === "var" ||
        node.arg.kind === "const" ||
        node.arg.kind === "not";
      if (style === "boolean") {
        return atom ? `${inner}′` : `(${inner})′`;
      }
      return atom ? `¬${inner}` : `¬(${inner})`;
    }
    default: {
      const own = PRECEDENCE[node.op];
      const wrap = (child: Formula, side: "left" | "right"): string => {
        const text = formatFormula(child, style, full);
        if (child.kind !== "bin") {
          return text;
        }
        const theirs = PRECEDENCE[child.op];
        const sameKind = child.op === node.op && ASSOCIATIVE.includes(node.op);
        let bracket = full || theirs < own;
        if (theirs === own && !sameKind) {
          bracket = true;
        }
        if (theirs === own && sameKind && side === "right") {
          bracket = full;
        }
        return bracket ? `(${text})` : text;
      };
      const joiner =
        style === "boolean" && node.op === "and"
          ? ""
          : ` ${SYMBOL[style][node.op]} `;
      return `${wrap(node.left, "left")}${joiner}${wrap(node.right, "right")}`;
    }
  }
}

// ---------------------------------------------------------------------------------------
// Tables
// ---------------------------------------------------------------------------------------

export interface TableColumn {
  node: Formula;
  label: string;
}

export interface TableRow {
  inputs: boolean[];
  values: boolean[];
  /** The row's minterm number, with the first variable as the most significant bit. */
  minterm: number;
}

export type RowOrder = "true-first" | "false-first";

export interface TruthTable {
  variables: string[];
  columns: TableColumn[];
  rows: TableRow[];
  /** Index of the column holding the whole formula. */
  finalIndex: number;
}

/** Sub-formulas that are not bare variables or constants, each once, innermost first. */
export function subformulas(
  formula: Formula,
  style: NotationStyle = "logic"
): TableColumn[] {
  const seen = new Map<string, TableColumn>();
  const walk = (node: Formula): void => {
    if (node.kind === "not") {
      walk(node.arg);
    } else if (node.kind === "bin") {
      walk(node.left);
      walk(node.right);
    }
    if (node.kind === "not" || node.kind === "bin") {
      const label = formatFormula(node, style, true);
      if (!seen.has(label)) {
        seen.set(label, { node, label });
      }
    }
  };
  walk(formula);
  return [...seen.values()];
}

function assignmentFor(
  variables: string[],
  index: number
): { env: Assignment; minterm: number } {
  const env: Assignment = {};
  const n = variables.length;
  variables.forEach((name, k) => {
    env[name] = Math.floor(index / 2 ** (n - 1 - k)) % 2 === 1;
  });
  return { env, minterm: index };
}

export function buildTruthTable(
  formula: Formula,
  variables: string[],
  options: { order: RowOrder; intermediate: boolean; style: NotationStyle }
): TruthTable {
  const all = subformulas(formula, options.style);
  const wanted = options.intermediate
    ? all
    : all.filter((column) => column.node === formula);
  const columns =
    wanted.length > 0 || (formula.kind !== "var" && formula.kind !== "const")
      ? wanted
      : [];
  const final: TableColumn =
    columns.at(-1)?.node === formula
      ? (columns.at(-1) as TableColumn)
      : { node: formula, label: formatFormula(formula, options.style) };
  const shown =
    columns.at(-1)?.node === formula ? columns : [...columns, final];
  const count = 2 ** variables.length;
  const rows: TableRow[] = [];
  for (let i = 0; i < count; i += 1) {
    const index = options.order === "true-first" ? count - 1 - i : i;
    const { env, minterm } = assignmentFor(variables, index);
    rows.push({
      inputs: variables.map((name) => env[name]),
      values: shown.map((column) => evaluate(column.node, env)),
      minterm,
    });
  }
  return { variables, columns: shown, rows, finalIndex: shown.length - 1 };
}

/** The formula's value for every minterm number, 0 … 2ⁿ − 1. */
export function truthVector(formula: Formula, variables: string[]): boolean[] {
  const count = 2 ** variables.length;
  const vector: boolean[] = [];
  for (let i = 0; i < count; i += 1) {
    vector.push(evaluate(formula, assignmentFor(variables, i).env));
  }
  return vector;
}

export type FormulaKind = "tautology" | "contradiction" | "contingent";

export interface Analysis {
  kind: FormulaKind;
  trueRows: number;
  totalRows: number;
  minterms: number[];
  maxterms: number[];
}

export function analyse(vector: boolean[]): Analysis {
  const minterms: number[] = [];
  const maxterms: number[] = [];
  vector.forEach((value, index) => {
    (value ? minterms : maxterms).push(index);
  });
  let kind: FormulaKind = "contingent";
  if (maxterms.length === 0) {
    kind = "tautology";
  } else if (minterms.length === 0) {
    kind = "contradiction";
  }
  return {
    kind,
    trueRows: minterms.length,
    totalRows: vector.length,
    minterms,
    maxterms,
  };
}

function literal(
  name: string,
  positive: boolean,
  style: NotationStyle
): string {
  if (positive) {
    return name;
  }
  return style === "boolean" ? `${name}′` : `¬${name}`;
}

/** The canonical (full) sum of products from the true rows, or `0`. */
export function canonicalDnf(
  variables: string[],
  minterms: number[],
  style: NotationStyle = "logic"
): string {
  if (minterms.length === 0) {
    return "0";
  }
  const n = variables.length;
  const terms = minterms.map((m) => {
    const lits = variables.map((name, k) =>
      literal(name, Math.floor(m / 2 ** (n - 1 - k)) % 2 === 1, style)
    );
    if (style === "boolean") {
      return lits.join("·");
    }
    return n > 1 && minterms.length > 1
      ? `(${lits.join(" ∧ ")})`
      : lits.join(" ∧ ");
  });
  return terms.join(style === "boolean" ? " + " : " ∨ ");
}

/** The canonical (full) product of sums from the false rows, or `1`. */
export function canonicalCnf(
  variables: string[],
  maxterms: number[],
  style: NotationStyle = "logic"
): string {
  if (maxterms.length === 0) {
    return "1";
  }
  const n = variables.length;
  const terms = maxterms.map((m) => {
    const lits = variables.map((name, k) =>
      literal(name, Math.floor(m / 2 ** (n - 1 - k)) % 2 === 0, style)
    );
    if (style === "boolean") {
      return n > 1 ? `(${lits.join(" + ")})` : lits.join(" + ");
    }
    return n > 1 && maxterms.length > 1
      ? `(${lits.join(" ∨ ")})`
      : lits.join(" ∨ ");
  });
  return terms.join(style === "boolean" ? "·" : " ∧ ");
}

export interface Comparison {
  equivalent: boolean;
  /** The first assignment where the two formulas differ, if they do. */
  counterexample: {
    assignment: Assignment;
    first: boolean;
    second: boolean;
  } | null;
  variables: string[];
}

export function compareFormulas(first: Formula, second: Formula): Comparison {
  const variables = [
    ...new Set([...variablesOf(first), ...variablesOf(second)]),
  ].sort(compareVariables);
  const count = 2 ** variables.length;
  for (let i = 0; i < count; i += 1) {
    const { env } = assignmentFor(variables, i);
    const a = evaluate(first, env);
    const b = evaluate(second, env);
    if (a !== b) {
      return {
        equivalent: false,
        counterexample: { assignment: env, first: a, second: b },
        variables,
      };
    }
  }
  return { equivalent: true, counterexample: null, variables };
}
